// Turning an approved run into a filed one.
//
// The steps a submission goes through, and why each exists:
//
//   generated  the 837P has been built and recorded, with the control number
//              of every claim in it. Nothing has left yet.
//   sent       a person uploaded it through the GAMMIS Web Portal. Until the
//              SFTP worker exists this is the shuttle, and it is a real state
//              rather than an assumption: a file that was generated and never
//              uploaded is the failure this flag exists to make visible.
//   accepted / partially_accepted / rejected
//              what the 824 said. Partial is the normal case, because Georgia
//              validates fee-for-service at claim level.
//
// This module is the pure part: what to record, what each answer means, and
// what a line's status should become. The writing lives in the page.

import { readAcknowledgement } from './acks.js';

export const SUBMISSION_STATUS = [
  'generated', 'sent', 'accepted', 'partially_accepted', 'rejected', 'superseded',
];

/** What a person should do next, given where a submission has got to. */
export const NEXT_STEP = {
  generated: 'Upload the file through the GAMMIS Web Portal, then mark it sent.',
  sent: 'Waiting on the 824. It arrives within one business day — download it and drop it in here.',
  accepted: 'Accepted. Claims are in adjudication; the remittance follows the Monday cycle.',
  partially_accepted: 'Some claims were rejected and the rest are processing. Fix the rejected ones and send them in a new run.',
  rejected: 'Nothing in this file was accepted. Correct the problems and generate again.',
  superseded: 'Replaced by a later submission for this run.',
};

/**
 * What to do next, given where this submission is and whether the SFTP worker
 * is configured. The wording matters: telling somebody to go and upload a file
 * by hand when the machine will do it in thirty seconds is how a good path
 * goes unused, and telling them it is automatic when it is not is worse.
 */
export const nextStep = (sub, { automatic = false } = {}) => {
  const status = sub?.status;
  if (status === 'generated' && automatic) {
    return 'Ready to send. Send to GAMMIS signs and encrypts the file and uploads it over SFTP.';
  }
  if (status === 'sent' && sub?.channel === 'sftp') {
    return 'Uploaded over SFTP. The 824 arrives within one business day and is collected automatically.';
  }
  return NEXT_STEP[status] || '';
};

/** SHA-256 of the file as sent, so what is stored can be proved to be it. */
export const sha256Hex = async (text) => {
  const bytes = new TextEncoder().encode(text);
  const digest = await crypto.subtle.digest('SHA-256', bytes);
  return Array.from(new Uint8Array(digest)).map((b) => b.toString(16).padStart(2, '0')).join('');
};

/** Pull the envelope control numbers back out of the file that was built. */
export const controlNumbersOf = (edi) => {
  const line = (id) => String(edi || '').split('\n').find((l) => l.startsWith(`${id}*`)) || '';
  const isa = line('ISA').split('*');
  const gs  = line('GS').split('*');
  const st  = line('ST').split('*');
  return {
    isa_control_number: isa[13] || null,
    gs_control_number: gs[6] || null,
    st_control_number: (st[2] || '').replace(/~$/, '') || null,
    usage_indicator: isa[15] || null,
  };
};

/**
 * A file name a person can recognise in a downloads folder and GAMMIS will
 * accept: no spaces, no punctuation beyond dashes, one dot before the
 * extension. `.dat` is the preferred extension in the companion guide.
 */
export const submissionFileName = ({ dateFrom, dateTo, usageIndicator = 'T', attempt = 1 }) =>
  `837P-${String(dateFrom).replace(/-/g, '')}-${String(dateTo).replace(/-/g, '')}`
  + `-${usageIndicator === 'P' ? 'PROD' : 'TEST'}${attempt > 1 ? `-${attempt}` : ''}.dat`;

/**
 * Read an acknowledgement and say what it means for this submission.
 *
 * Returns the columns to write plus the problems in sentences. An
 * unrecognised file is reported rather than swallowed — a submitter who drops
 * in the wrong download should be told, not left looking at an unchanged
 * screen that reads like silence.
 */
export const applyAcknowledgement = (text) => {
  const ack = readAcknowledgement(text);
  if (!ack) {
    return { ok: false, error: 'That file is not an EDI interchange. Download the acknowledgement from the GAMMIS portal and try again.' };
  }
  const now = new Date().toISOString();

  if (ack.kind === 'TA1') {
    return {
      ok: true, kind: 'TA1', accepted: ack.accepted,
      update: {
        ta1_received_at: now,
        ta1_accepted: ack.accepted,
        ta1_raw: text,
        // A rejected envelope was never opened, so the whole file is rejected
        // and no 824 will follow.
        ...(ack.accepted ? {} : { status: 'rejected', problems: ack.problems }),
      },
      problems: ack.problems,
    };
  }

  if (ack.kind === '824') {
    const status = ack.status === 'accepted' ? 'accepted'
      : ack.status === 'partially_accepted' ? 'partially_accepted'
      : 'rejected';
    return {
      ok: true, kind: '824', accepted: ack.accepted, status,
      update: {
        ack_824_received_at: now,
        ack_824_status: ack.status,
        ack_824_raw: text,
        status,
        problems: ack.problems.length ? ack.problems : null,
      },
      problems: ack.problems,
    };
  }

  if (ack.kind === '999') {
    // A 999 answers 270 and 276 files, not 837s. Saying so beats recording it
    // against a claim submission it does not describe.
    return { ok: false, error: 'That is a 999, which acknowledges an eligibility or claim-status file. An 837 is answered by an 824.' };
  }

  return { ok: false, error: ack.problems?.[0] || 'That file carries no acknowledgement this can read.' };
};

/**
 * What a claim line's status should become once a submission is answered.
 *
 * On a partial acceptance the 824 names what failed at claim level, but
 * mapping those back to individual lines needs the CLM01 in its CTX — so
 * until that is proven against a real 824 this leaves partially accepted
 * lines as submitted rather than guessing which ones were the casualties.
 */
export const lineStatusAfter = (submissionStatus) => {
  if (submissionStatus === 'accepted') return 'submitted';
  if (submissionStatus === 'rejected') return 'approved';
  return null;
};

/**
 * A billing run's own status around filing. Sending a file makes the run
 * 'submitted', which locks its lines against edits. A rejected file puts its
 * lines back to 'approved' (lineStatusAfter) so they can be corrected and
 * re-filed, so the run reopens too — unless another of its files is still
 * with Gainwell or was accepted, in which case the run is still filed.
 */
export const RUN_STATUS_WHEN_SENT = 'submitted';
const LIVE_SUBMISSION = ['sent', 'accepted', 'partially_accepted'];
export const runReopensAfter = (submissionStatus, otherSubmissionStatuses = []) =>
  submissionStatus === 'rejected' && !otherSubmissionStatuses.some(s => LIVE_SUBMISSION.includes(s));
